/**
 * StaffDetailView — Individual employee dashboard
 * Shows: contract info, pay period hours, overtime balance, vacation balance,
 * surcharges, leave history, period-by-period breakdown
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useWorkforceStore } from '../../stores';
import { supabase } from '../../lib/supabase';
import type { Staff, LeaveType } from '../../stores/workforce-store';
import type { ScheduleEntry } from '../../stores/schedule-store';
import { ShiftTypeSelector } from '../uren/ShiftTypeSelector';
import type { ShiftTypeValue } from '../../lib/shift-labels';
import {
  getPayPeriod,
  getPayPeriodsForYear,
  breakForRange,
  calculateSurcharges,
  calculateOvertime,
  calculateVacationAccrual,
  calculateScheduledHours,
  calculateYearSaldo,
  formatHours,
  formatEuro,
  type SurchargeResult,
  type ScheduledHoursResult,
} from '../../lib/hours-calculator';
import {
  LEAVE_TYPES,
  getLeaveLabel,
  getLeaveColor,
  filterSickLeave,
  filterNonSickLeave,
  filterVacationUsed,
} from '../../lib/leave-types';
import { getCaoUurloon, effectieveDienstjaren } from '../../lib/cao-salary';
import { LeaveDialog } from './LeaveDialog';
import { LeaveOnBehalfDialog } from './LeaveOnBehalfDialog';
import { apiFetch } from '../../stores/workforce-store';
import { calculatePotjeSaldo, type Mutation } from '../../lib/urensaldo-calculator';
import { JaarurenStandCard } from './JaarurenStandCard';
import { regimeSegments } from '../../lib/contract-regime';
import type { VakantieSaldo } from '../../lib/vakantie-saldo';
import { VakantieLagenKaart } from './VakantieLagenKaart';

interface StaffDetailViewProps {
  staff: Staff;
  onBack: () => void;
  onEdit: () => void;
}

const contractLabels: Record<string, string> = {
  vast: 'Vast contract',
  jaaruren: 'Jaaruren-contract',
  'min-max': 'Min-max contract',
  'vast-salaris': 'Vast salaris',
  '0-uren': '0-uren contract',
  student: 'Student',
  zzp: 'ZZP',
};

// Leave type labels and colors imported from lib/leave-types

// Helper to extract HH:mm from clock_in/clock_out
function toHHmm(val: string | null | undefined): string | null {
  if (!val) return null;
  if (val.includes('T')) {
    const d = new Date(val);
    if (isNaN(d.getTime())) return null;
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return val.slice(0, 5);
}

export function StaffDetailView({ staff, onBack, onEdit }: StaffDetailViewProps) {
  const { fetchTimeEntries, timeEntries, updateTimeEntry, deleteTimeEntry, createTimeEntry, fetchLeave, leaveEntries, createLeave, deleteLeave, fetchEntitlements, leaveEntitlements, createEntitlement, deleteEntitlement, fetchSickLeave, sickLeaves, reportSick, reportRecovered, contracts, fetchContracts, fetchVakantieSaldo } = useWorkforceStore();

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showOnBehalfDialog, setShowOnBehalfDialog] = useState(false);
  const [showEntitlementForm, setShowEntitlementForm] = useState(false);
  const [scheduleEntries, setScheduleEntries] = useState<ScheduleEntry[]>([]);
  const [expandedPeriod, setExpandedPeriod] = useState<string | null>(null);
  const [showSickDialog, setShowSickDialog] = useState(false);
  const [sickDate, setSickDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [sickNotes, setSickNotes] = useState('');
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false);
  // Laatste ziektedag, niet de eerste werkdag. Default vandaag, maar
  // aanpasbaar: een vergeten betermelding hoort achteraf te kunnen.
  const [recoveryDate, setRecoveryDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [isSickAction, setIsSickAction] = useState(false);

  // Inline editing state for time entries
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ clock_in?: string; clock_out?: string; break_minutes?: number; notes?: string }>({});
  const [breakManuallyEdited, setBreakManuallyEdited] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [addingEntry, setAddingEntry] = useState<string | null>(null); // period "from" key when adding
  const [newEntry, setNewEntry] = useState({ date: '', type: '', clock_in: '', clock_out: '', break_minutes: 0 });
  const [isSaving, setIsSaving] = useState(false);
  // Fout uit de laatste entry-mutatie (bv. 423 periode-vergrendeld) — anders faalt opslaan stil
  const [entryError, setEntryError] = useState<string | null>(null);

  function mutationErrorText(fallback: string): string {
    const err = useWorkforceStore.getState().lastMutationError;
    return (err?.body?.message as string) || err?.message || fallback;
  }

  const SHIFT_TYPES = ['Wasserij', 'Chauffeur', 'Kantoor'] as const;
  const shiftColors: Record<string, string> = {
    'Wasserij': 'bg-blue-100 text-blue-700',
    'Chauffeur': 'bg-emerald-100 text-emerald-700',
    'Kantoor': 'bg-purple-100 text-purple-700',
  };

  function extractShiftType(notes: string | null): string {
    return notes?.match(/—\s*(.+)$/)?.[1]?.trim() || '';
  }

  function buildNotes(currentNotes: string | null, newType: string): string {
    const prefix = currentNotes?.replace(/\s*—\s*.+$/, '').trim() || '';
    if (!newType) return prefix;
    return prefix ? `${prefix} — ${newType}` : `— ${newType}`;
  }

  function toISOTimestamp(date: string, time: string): string {
    // Build ISO timestamp in Europe/Amsterdam timezone
    // date: YYYY-MM-DD, time: HH:mm
    const [h, m] = time.split(':').map(Number);
    const d = new Date(`${date}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`);
    // Approximate: construct in local time (browser in NL timezone)
    return d.toISOString();
  }

  async function handleSaveEdit(entryId: string, entry: { date: string; clock_in: string; clock_out: string | null; break_minutes: number; notes: string | null }) {
    setIsSaving(true);
    const updates: Record<string, unknown> = {};
    if (editValues.clock_in !== undefined && editValues.clock_in !== toHHmm(entry.clock_in)) {
      updates.clock_in = toISOTimestamp(entry.date, editValues.clock_in);
    }
    if (editValues.clock_out !== undefined && editValues.clock_out !== (toHHmm(entry.clock_out) || '')) {
      updates.clock_out = toISOTimestamp(entry.date, editValues.clock_out);
    }
    if (editValues.break_minutes !== undefined && editValues.break_minutes !== entry.break_minutes) {
      updates.break_minutes = editValues.break_minutes;
    }
    if (editValues.notes !== undefined && editValues.notes !== entry.notes) {
      updates.notes = editValues.notes;
    }
    if (Object.keys(updates).length > 0) {
      setEntryError(null);
      const result = await updateTimeEntry(entryId, updates as { clock_in?: string; clock_out?: string; break_minutes?: number; notes?: string });
      if (!result) {
        // Bewerking open laten zodat niets stil verloren gaat (bv. periode-lock 423)
        setEntryError(mutationErrorText('Opslaan mislukt'));
        setIsSaving(false);
        return;
      }
    }
    setEditingEntryId(null);
    setEditValues({});
    setBreakManuallyEdited(false);
    setIsSaving(false);
  }

  function startEditing(te: { id: string; clock_in: string; clock_out: string | null; break_minutes: number; notes: string | null }) {
    setEditingEntryId(te.id);
    setEditValues({
      clock_in: toHHmm(te.clock_in) || '',
      clock_out: toHHmm(te.clock_out) || '',
      break_minutes: te.break_minutes,
      notes: te.notes || '',
    });
    setBreakManuallyEdited(false);
  }

  function cancelEditing() {
    setEditingEntryId(null);
    setEditValues({});
    setBreakManuallyEdited(false);
    setEntryError(null);
  }

  function recalcBreak(clockIn: string, clockOut: string, notes: string | null) {
    const shiftType = extractShiftType(notes)?.toLowerCase() || (staff.team || '');
    return breakForRange(shiftType, clockIn, clockOut).breakMinutes;
  }

  async function handleDeleteEntry(id: string) {
    setIsSaving(true);
    setEntryError(null);
    const ok = await deleteTimeEntry(id);
    if (!ok) setEntryError(mutationErrorText('Verwijderen mislukt'));
    setConfirmDeleteId(null);
    setIsSaving(false);
  }

  async function handleCreateEntry() {
    if (!newEntry.clock_in || !newEntry.clock_out || !newEntry.date) return;
    setIsSaving(true);
    setEntryError(null);
    const notes = newEntry.type ? `— ${newEntry.type}` : null;
    const result = await createTimeEntry({
      staff_id: staff.id,
      date: newEntry.date,
      clock_in: toISOTimestamp(newEntry.date, newEntry.clock_in),
      clock_out: toISOTimestamp(newEntry.date, newEntry.clock_out),
      break_minutes: newEntry.break_minutes,
      notes: notes || undefined,
    });
    if (!result) {
      // Invoer open laten: gebruiker ziet de reden (bv. periode geëxporteerd naar NMBRS)
      setEntryError(mutationErrorText('Opslaan mislukt'));
      setIsSaving(false);
      return;
    }
    setAddingEntry(null);
    setNewEntry({ date: '', type: '', clock_in: '', clock_out: '', break_minutes: 0 });
    setIsSaving(false);
  }

  const currentPeriod = getPayPeriod(new Date());
  const periods = useMemo(() => getPayPeriodsForYear(selectedYear), [selectedYear]);

  const isZzp = staff.contract_type === 'zzp' || staff.contract_type === 'dga';
  const isAllIn = staff.loon_type === 'all-in';
  const isVast = staff.contract_type === 'vast';
  const isMinMax = staff.contract_type === 'min-max';
  const isJaaruren = staff.contract_type === 'jaaruren';
  const isVastSalaris = staff.contract_type === 'vast-salaris';

  // Contracthistorie = regimehistorie (vakantiebasis per segment, zie lib/contract-regime.ts)
  useEffect(() => { fetchContracts(staff.id); }, [staff.id, fetchContracts]);
  const staffContracts = useMemo(() => contracts.filter(c => c.staff_id === staff.id), [contracts, staff.id]);
  const regimeSegs = useMemo(() => regimeSegments(selectedYear, staffContracts, staff), [selectedYear, staffContracts, staff]);

  // CAO uurloon calculation (incl. erkende ervaringsjaren elders)
  const caoResult = useMemo(() => {
    if (!staff.loongroep || isZzp) return null;
    // All-in: alleen leeftijd nodig. Bruto: datum_in_dienst vereist.
    if (staff.loon_type !== 'all-in' && !staff.datum_in_dienst) return null;
    return getCaoUurloon(
      staff.loongroep,
      staff.datum_in_dienst || '2020-01-01',
      staff.geboortedatum,
      staff.loon_type,
      undefined,
      effectieveDienstjaren(staff.datum_in_dienst, staff.geboortedatum, staff.cao_ervaringsjaren),
    );
  }, [staff.loongroep, staff.datum_in_dienst, staff.geboortedatum, staff.loon_type, staff.cao_ervaringsjaren, isZzp]);

  // Fetch year data — calendar year (jan 1 t/m dec 31)
  useEffect(() => {
    const yearFrom = `${selectedYear}-01-01`;
    const yearTo = `${selectedYear}-12-31`;
    fetchTimeEntries({ staff_id: staff.id, from: yearFrom, to: yearTo });
    fetchLeave({ staff_id: staff.id, from: yearFrom, to: yearTo });
    fetchEntitlements(staff.id);
    fetchSickLeave(staff.id);
    // Fetch schedule for the year
    const fetchSchedule = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const res = await fetch(`/api/workforce/schedule?from=${yearFrom}&to=${yearTo}&staff_id=${staff.id}`, {
          headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        });
        if (res.ok) {
          const data = await res.json();
          setScheduleEntries(data);
        }
      } catch { /* ignore */ }
    };
    fetchSchedule();
  }, [staff.id, selectedYear, fetchTimeEntries, fetchLeave]);

  // Calculate period summaries
  const periodSummaries = useMemo(() => {
    return periods.map(period => {
      const periodTimeEntries = timeEntries.filter(
        te => te.date >= period.from && te.date <= period.to
      );
      const periodLeave = leaveEntries.filter(
        le => le.date >= period.from && le.date <= period.to
      );
      const periodSchedule = scheduleEntries.filter(
        se => se.date >= period.from && se.date <= period.to
      );

      // Worked hours from time entries (actual clock data)
      let workedHours = 0;
      let totalSurcharges: SurchargeResult = { normalHours: 0, surchargeHours: 0, surchargeAmount: 0, breakdown: [] };

      if (periodTimeEntries.length > 0) {
        for (const te of periodTimeEntries) {
          if (te.total_hours) workedHours += te.total_hours;
          // Calculate surcharges if we have clock times and uurloon
          if (te.clock_in && te.clock_out && staff.uurloon) {
            const clockIn = toHHmm(te.clock_in);
            const clockOut = toHHmm(te.clock_out);
            if (clockIn && clockOut) {
              const sc = calculateSurcharges(clockIn, clockOut, te.date, staff.uurloon, te.break_minutes ?? 0, te.paid_break_minutes ?? 0);
              totalSurcharges.normalHours += sc.normalHours;
              totalSurcharges.surchargeHours += sc.surchargeHours;
              totalSurcharges.surchargeAmount += sc.surchargeAmount;
              // Merge breakdown
              for (const b of sc.breakdown) {
                const existing = totalSurcharges.breakdown.find(x => x.slot === b.slot);
                if (existing) {
                  existing.hours += b.hours;
                  existing.amount += b.amount;
                } else {
                  totalSurcharges.breakdown.push({ ...b });
                }
              }
            }
          }
        }
      }

      // Leave hours
      const leaveHours = filterNonSickLeave(periodLeave)
        .reduce((sum, l) => sum + l.hours, 0);
      const sickHours = filterSickLeave(periodLeave)
        .reduce((sum, l) => sum + l.hours, 0);
      const vacationTaken = filterVacationUsed(periodLeave)
        .reduce((sum, l) => sum + l.hours, 0);

      // Overtime
      const overtime = calculateOvertime(workedHours);

      // Vacation accrual
      const vacationAccrual = calculateVacationAccrual(overtime.paidHours);

      return {
        period,
        workedHours,
        paidHours: overtime.paidHours,
        overtimeSurplus: overtime.overtimeSurplus,
        surcharges: totalSurcharges,
        vacationAccrual,
        leaveHours,
        sickHours,
        vacationTaken,
      };
    });
  }, [periods, timeEntries, leaveEntries, scheduleEntries, staff.uurloon]);

  // Potje-saldo uit urensaldo_mutations (niet meer on-the-fly uit time_entries,
  // want na een NMBRS-export kunnen er withdrawals zijn die surplus verlagen).
  const [potjeMutations, setPotjeMutations] = useState<Mutation[]>([]);
  useEffect(() => {
    if (staff.contract_type === 'zzp' || staff.contract_type === 'dga') return;
    let cancelled = false;
    (async () => {
      try {
        const data: Mutation[] = await apiFetch(`/api/workforce/urensaldo-mutations?staff_id=${staff.id}`);
        if (!cancelled) setPotjeMutations(data || []);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [staff.id, staff.contract_type]);
  const cumulativeOvertime = calculatePotjeSaldo(potjeMutations);

  // Vakantie: één getal uit het primitief (api/_lib/vakantie-saldo.ts via GET /api/workforce/vakantie-saldo):
  // lagen wettelijk/bovenwettelijk per jaar met vervaldatum, FIFO-afboeking, verval volgt kennisgeving.
  // Dezelfde bron als de goedkeurkaart en de team-app; de kaart rekent zelf niets meer.
  // Peildatum: vandaag, of 31-12 van een gekozen eerder jaar.
  const contractStart = staff.datum_in_dienst || staff.start_date || null;
  const [vakantieSaldo, setVakantieSaldo] = useState<VakantieSaldo | null>(null);
  const [vakantieLaden, setVakantieLaden] = useState(false);
  const vakantiePeildatum = selectedYear < new Date().getFullYear() ? `${selectedYear}-12-31` : undefined;
  useEffect(() => {
    if (isZzp) return;
    let cancelled = false;
    setVakantieLaden(true);
    fetchVakantieSaldo(staff.id, vakantiePeildatum)
      .then(r => { if (!cancelled) setVakantieSaldo(r?.saldo ?? null); })
      .finally(() => { if (!cancelled) setVakantieLaden(false); });
    return () => { cancelled = true; };
  }, [staff.id, vakantiePeildatum, isZzp, fetchVakantieSaldo, leaveEntries.length, leaveEntitlements.length]);
  const regimeVoetnoot = regimeSegs.length > 1 ? (
    <p className="text-[10px] text-muted mt-1">
      {regimeSegs.map(s => `${contractLabels[s.vorm] || s.vorm}${s.urenPerWeek ? ` ${s.urenPerWeek}u` : ''} ${s.from.slice(8)}-${s.from.slice(5, 7)} t/m ${s.to.slice(8)}-${s.to.slice(5, 7)}`).join(' · ')}
    </p>
  ) : undefined;

  // Current period summary
  const currentSummary = periodSummaries.find(ps => ps.period.from === currentPeriod.from);

  // ── Urensaldo (vast + min-max met werkrooster; min-max = indicatief) ──
  const hasWerkrooster = (isVast || isMinMax) && staff.werkrooster && staff.werkrooster.length > 0;
  const saldoIndicatief = isMinMax && hasWerkrooster;

  const currentScheduled: ScheduledHoursResult | null = useMemo(() => {
    if (!hasWerkrooster) return null;
    // Clamp to today so future scheduled hours don't count as deficit
    const today = format(new Date(), 'yyyy-MM-dd');
    const effectiveTo = currentPeriod.to > today ? today : currentPeriod.to;
    const periodTimeEntries = timeEntries.filter(te => te.date >= currentPeriod.from && te.date <= effectiveTo);
    const periodLeave = leaveEntries.filter(le => le.date >= currentPeriod.from && le.date <= effectiveTo);
    return calculateScheduledHours(staff.werkrooster, staff.team, currentPeriod.from, effectiveTo, periodTimeEntries, periodLeave.map(l => ({ date: l.date, hours: l.hours, leave_type: l.leave_type })), contractStart, scheduleEntries);
  }, [hasWerkrooster, staff.werkrooster, staff.team, currentPeriod, timeEntries, leaveEntries, contractStart, scheduleEntries]);

  // Beginsaldo uren: carry-over from entitlements (e.g. overdracht vorig jaar)
  const urenBeginsaldo = leaveEntitlements
    .filter(ent => (ent.leave_type as string) === 'urensaldo')
    .reduce((sum, ent) => sum + ent.total_hours, 0);

  // Cumulative saldo: beginsaldo + (gewerkt + verlof - verwacht) from Jan 1 to today
  const yearSaldoResult = useMemo(() => {
    if (!hasWerkrooster) return null;
    return calculateYearSaldo(
      staff.werkrooster, staff.team, selectedYear,
      timeEntries, leaveEntries.map(l => ({ date: l.date, hours: l.hours, leave_type: l.leave_type })),
      urenBeginsaldo, contractStart, scheduleEntries,
    );
  }, [hasWerkrooster, staff.werkrooster, staff.team, selectedYear, timeEntries, leaveEntries, urenBeginsaldo, contractStart, scheduleEntries]);

  const yearSaldo = yearSaldoResult?.yearSaldo ?? null;
  const monthlyBreakdown = yearSaldoResult?.monthlyBreakdown ?? [];

  // Staff leave entries for display
  const staffLeave = leaveEntries
    .filter(le => le.staff_id === staff.id)
    .sort((a, b) => b.date.localeCompare(a.date));

  const handleCreateLeave = useCallback(async (data: { staff_id: string; date: string; leave_type: string; hours: number; notes?: string }) => {
    const result = await createLeave(data as Parameters<typeof createLeave>[0]);
    return !!result;
  }, [createLeave]);

  const handleDeleteLeave = useCallback(async (id: string) => {
    const result = await deleteLeave(id);
    if (result) setShowLeaveDialog(false);
    return result;
  }, [deleteLeave]);

  // Active sick leave
  const activeSickLeave = sickLeaves.find(sl => sl.staff_id === staff.id && !sl.end_date);

  const handleReportSick = async () => {
    setIsSickAction(true);
    const result = await reportSick(staff.id, sickDate, sickNotes || undefined);
    if (result) {
      setShowSickDialog(false);
      setSickNotes('');
      // Refresh leave entries to show new ziekte entries
      const yearFrom = `${selectedYear}-01-01`;
      const yearTo = `${selectedYear}-12-31`;
      fetchLeave({ staff_id: staff.id, from: yearFrom, to: yearTo });
    }
    setIsSickAction(false);
  };

  const handleReportRecovered = async () => {
    if (!activeSickLeave) return;
    setIsSickAction(true);
    await reportRecovered(activeSickLeave.id, recoveryDate);
    const yearFrom = `${selectedYear}-01-01`;
    const yearTo = `${selectedYear}-12-31`;
    fetchLeave({ staff_id: staff.id, from: yearFrom, to: yearTo });
    fetchSickLeave(staff.id);
    setShowRecoveryDialog(false);
    setIsSickAction(false);
  };

  return (
    <div className="space-y-6">
      {/* Header — compact info bar */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted">
            {staff.team && <span className="capitalize">{staff.team}</span>}
            {staff.team && ' · '}
            {contractLabels[staff.contract_type]}
            {staff.uren_per_week && ` · ${staff.uren_per_week}u/week`}
            {staff.uurloon && ` · ${formatEuro(staff.uurloon)}/u`}
            {staff.loongroep && ` · LG ${staff.loongroep}`}
          </p>
          {staff.datum_in_dienst && (
            <p className="text-xs text-muted">
              In dienst sinds {format(new Date(staff.datum_in_dienst + 'T00:00:00'), 'd MMM yyyy', { locale: nl })}
              {caoResult && (
                <span>
                  {' · '}{caoResult.isAllIn
                    ? `All-in (${caoResult.leeftijd >= 20 ? '20+' : caoResult.leeftijd + ' jaar'})`
                    : caoResult.isYouth ? `${caoResult.leeftijd} jaar`
                    : `Trede ${caoResult.trede} (${caoResult.dienstjaren} dj)`
                  }
                </span>
              )}
            </p>
          )}
          {caoResult && staff.uurloon && Math.abs(staff.uurloon - caoResult.uurloon) > 0.01 && (
            <p className="text-xs mt-0.5">
              <span className="text-muted">CAO uurloon: </span>
              <span className="font-medium text-main">{formatEuro(caoResult.uurloon)}</span>
              <span className={staff.uurloon < caoResult.uurloon ? 'text-red-600 ml-2' : 'text-emerald-600 ml-2'}>
                {staff.uurloon < caoResult.uurloon
                  ? `(huidig ${formatEuro(staff.uurloon)} — onder CAO!)`
                  : `(huidig ${formatEuro(staff.uurloon)} — boven CAO)`
                }
              </span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isZzp && !activeSickLeave && (
            <button
              onClick={() => setShowSickDialog(true)}
              className="px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              Ziek melden
            </button>
          )}
          {activeSickLeave && (
            <button
              onClick={() => setShowRecoveryDialog(true)}
              disabled={isSickAction}
              className="px-3 py-2 text-sm font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
            >
              {isSickAction ? 'Verwerken...' : 'Beter melden'}
            </button>
          )}
        </div>
      </div>

      {/* Waarschuwing: team 'algemeen' met werkrooster — pauzeregels vallen op wettelijk minimum */}
      {hasWerkrooster && (!staff.team || staff.team === 'algemeen') && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
          <p className="text-sm font-medium text-amber-800">Team "Algemeen" met werkrooster</p>
          <p className="text-xs text-amber-700 mt-0.5">
            Pauzeregels vallen terug op het wettelijk minimum (30 min boven 5,5u, anders geen pauze).
            Kies wasserij, route of kantoor bij "Bewerken" voor een correct urensaldo.
          </p>
        </div>
      )}

      {/* Sick leave banner */}
      {activeSickLeave && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-red-800">Ziek gemeld</p>
            <p className="text-xs text-red-600">
              Sinds {format(new Date(activeSickLeave.start_date + 'T00:00:00'), 'd MMMM yyyy', { locale: nl })}
              {activeSickLeave.notes && ` — ${activeSickLeave.notes}`}
            </p>
          </div>
        </div>
      )}

      {/* Recovery dialog */}
      {showRecoveryDialog && activeSickLeave && (
        <div className="bg-white border border-emerald-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-main mb-3">Beter melden</h3>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Laatste ziektedag</label>
              <input
                type="date"
                value={recoveryDate}
                min={activeSickLeave.start_date}
                onChange={e => setRecoveryDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
              <p className="text-[11px] text-muted mt-1">
                De eerste werkdag is de dag hierna. Ziekte-uren na deze datum worden teruggedraaid.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleReportRecovered}
              disabled={isSickAction}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
            >
              {isSickAction ? 'Verwerken...' : 'Beter melden'}
            </button>
            <button onClick={() => setShowRecoveryDialog(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">
              Annuleren
            </button>
          </div>
        </div>
      )}

      {/* Sick dialog */}
      {showSickDialog && (
        <div className="bg-white border border-red-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-main mb-3">Ziek melden</h3>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Eerste ziektedag</label>
              <input
                type="date"
                value={sickDate}
                onChange={e => setSickDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Opmerking</label>
              <input
                type="text"
                value={sickNotes}
                onChange={e => setSickNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                placeholder="bijv. griep"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleReportSick}
              disabled={isSickAction}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {isSickAction ? 'Verwerken...' : 'Ziek melden'}
            </button>
            <button onClick={() => setShowSickDialog(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">
              Annuleren
            </button>
          </div>
        </div>
      )}

      {/* Summary cards */}
      {!isZzp && (
        (isJaaruren || isVastSalaris) ? (
          /* ── Jaaruren: pace-grafiek + Vakantie + Ziekte · Vast-salaris: Vakantie + Ziekte ── */
          <div className="space-y-4">
            {isJaaruren && <JaarurenStandCard staffId={staff.id} year={selectedYear} />}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Vakantie-uren in lagen (wettelijk/bovenwettelijk met vervaldatum) */}
              <VakantieLagenKaart titel={`Vakantie-uren ${selectedYear}`} saldo={vakantieSaldo} laden={vakantieLaden} voetnoot={regimeVoetnoot} />

              {/* Ziekteverzuim */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <p className="text-xs font-medium text-muted uppercase tracking-wider mb-1">Ziekteverzuim {selectedYear}</p>
                {(() => {
                  const sickDays = filterSickLeave(leaveEntries).filter(l => l.staff_id === staff.id).length;
                  return (
                    <>
                      <p className="text-2xl font-semibold text-main mt-2">{sickDays} {sickDays === 1 ? 'dag' : 'dagen'}</p>
                      <p className="text-xs mt-1">
                        {activeSickLeave
                          ? <span className="text-red-600 font-medium">Ziek sinds {format(new Date(activeSickLeave.start_date + 'T00:00:00'), 'd MMM', { locale: nl })}</span>
                          : <span className="text-emerald-600">Niet ziek</span>
                        }
                      </p>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        ) : hasWerkrooster ? (
          /* ── Vast/min-max: 3 cards (Urensaldo, Vakantie, Ziekte) ── */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Urensaldo */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-xs font-medium text-muted uppercase tracking-wider mb-1">
                Urensaldo {selectedYear}
                {saldoIndicatief && (
                  <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-medium normal-case tracking-normal">
                    indicatief (min-max)
                  </span>
                )}
              </p>
              <p className={`text-2xl font-semibold mt-2 ${(yearSaldo ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {yearSaldo !== null ? `${yearSaldo >= 0 ? '+' : ''}${formatHours(yearSaldo)}` : '-'}
              </p>
              {urenBeginsaldo !== 0 && (
                <p className="text-[10px] text-muted mt-1">
                  Beginsaldo: {urenBeginsaldo >= 0 ? '+' : ''}{formatHours(urenBeginsaldo)}
                  {yearSaldo !== null && ` | Mutatie: ${(yearSaldo - urenBeginsaldo) >= 0 ? '+' : ''}${formatHours(yearSaldo - urenBeginsaldo)}`}
                </p>
              )}
              {currentScheduled && (
                <p className={`text-xs mt-1 ${currentScheduled.saldo >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {currentScheduled.saldo >= 0 ? '+' : ''}{formatHours(currentScheduled.saldo)} deze periode
                </p>
              )}
              {currentScheduled && currentScheduled.totalScheduled > 0 && (
                <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${(yearSaldo ?? 0) >= 0 ? 'bg-emerald-500' : 'bg-red-400'}`}
                    style={{ width: `${Math.min(100, Math.max(0, ((currentScheduled.totalActual + currentScheduled.totalLeave) / currentScheduled.totalScheduled) * 100))}%` }}
                  />
                </div>
              )}
              {yearSaldo !== null && Math.abs(yearSaldo) > 16 && (
                <p className="text-xs text-amber-600 font-medium mt-2">
                  {yearSaldo > 16 ? 'Overweeg uren af te bouwen' : 'Overweeg extra uren in te roosteren'}
                </p>
              )}
            </div>

            {/* Vakantie-uren */}
            {!isAllIn && (
              <VakantieLagenKaart titel={`Vakantie-uren ${selectedYear}`} saldo={vakantieSaldo} laden={vakantieLaden} voetnoot={regimeVoetnoot} />
            )}

            {/* Ziekteverzuim */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-xs font-medium text-muted uppercase tracking-wider mb-1">Ziekteverzuim {selectedYear}</p>
              {(() => {
                const sickDays = filterSickLeave(leaveEntries).filter(l => l.staff_id === staff.id).length;
                return (
                  <>
                    <p className="text-2xl font-semibold text-main mt-2">{sickDays} {sickDays === 1 ? 'dag' : 'dagen'}</p>
                    <p className="text-xs mt-1">
                      {activeSickLeave
                        ? <span className="text-red-600 font-medium">Ziek sinds {format(new Date(activeSickLeave.start_date + 'T00:00:00'), 'd MMM', { locale: nl })}</span>
                        : <span className="text-emerald-600">Niet ziek</span>
                      }
                    </p>
                  </>
                );
              })()}
            </div>
          </div>
        ) : isMinMax ? (
          /* ── Min-max: 3 cards (Deze periode, Vakantie basis+extra, Ziekte) ── */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Current period */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-xs font-medium text-muted uppercase tracking-wider mb-1">Deze periode</p>
              <p className="text-[10px] text-muted mb-3">{currentPeriod.label}</p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted">Gewerkt</span>
                  <span className="text-sm font-semibold text-main">{formatHours(currentSummary?.workedHours || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted">Uitbetaald</span>
                  <span className="text-sm font-medium text-main">{formatHours(currentSummary?.paidHours || 0)}</span>
                </div>
                {currentSummary && currentSummary.overtimeSurplus > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted">Overurenpotje</span>
                    <span className="text-sm font-medium text-indigo-600">+{formatHours(currentSummary.overtimeSurplus)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Vakantie-uren: basis tegoed + 10% extra opbouw */}
            {!isAllIn && (
              <VakantieLagenKaart titel={`Vakantie-uren ${selectedYear}`} saldo={vakantieSaldo} laden={vakantieLaden} voetnoot={regimeVoetnoot} />
            )}

            {/* Ziekteverzuim */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-xs font-medium text-muted uppercase tracking-wider mb-1">Ziekteverzuim {selectedYear}</p>
              {(() => {
                const sickDays = filterSickLeave(leaveEntries).filter(l => l.staff_id === staff.id).length;
                return (
                  <>
                    <p className="text-2xl font-semibold text-main mt-2">{sickDays} {sickDays === 1 ? 'dag' : 'dagen'}</p>
                    <p className="text-xs mt-1">
                      {activeSickLeave
                        ? <span className="text-red-600 font-medium">Ziek sinds {format(new Date(activeSickLeave.start_date + 'T00:00:00'), 'd MMM', { locale: nl })}</span>
                        : <span className="text-emerald-600">Niet ziek</span>
                      }
                    </p>
                  </>
                );
              })()}
            </div>
          </div>
        ) : (
          /* ── 0-uren/student: original cards (Deze periode, Overurenpotje, Vakantie) ── */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Current period */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-xs font-medium text-muted uppercase tracking-wider mb-1">Deze periode</p>
              <p className="text-[10px] text-muted mb-3">{currentPeriod.label} ({currentPeriod.from.slice(5)} — {currentPeriod.to.slice(5)})</p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted">Gewerkt</span>
                  <span className="text-sm font-semibold text-main">{formatHours(currentSummary?.workedHours || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted">Uitbetaald</span>
                  <span className="text-sm font-medium text-main">{formatHours(currentSummary?.paidHours || 0)}</span>
                </div>
                {(currentSummary?.surcharges.surchargeAmount || 0) > 0 && (
                  <>
                    {currentSummary?.surcharges.breakdown.map(b => (
                      <div key={b.slot} className="flex justify-between">
                        <span className="text-sm text-muted">Toeslag {b.slot}</span>
                        <span className="text-sm font-medium text-emerald-600">{formatHours(b.hours)} = {formatEuro(b.amount)}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>

            {/* Overtime balance */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-xs font-medium text-muted uppercase tracking-wider mb-1">Overurenpotje</p>
              <p className="text-2xl font-semibold text-main mt-2">{formatHours(cumulativeOvertime)}</p>
              {currentSummary && currentSummary.overtimeSurplus > 0 && (
                <p className="text-xs text-emerald-600 mt-1">+{formatHours(currentSummary.overtimeSurplus)} deze periode</p>
              )}
              <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500 rounded-full transition-all"
                  style={{ width: `${Math.min(100, (cumulativeOvertime / 40) * 100)}%` }}
                />
              </div>
            </div>

            {/* Vacation balance */}
            {!isAllIn && (
              <VakantieLagenKaart
                titel={`Vakantie-uren ${selectedYear}`}
                saldo={vakantieSaldo}
                laden={vakantieLaden}
                voetnoot={<p className="text-[10px] text-muted mt-1">10% opbouw over betaalde uren, in de wettelijke laag van het jaar</p>}
              />
            )}
          </div>
        )
      )}

      {/* Month overview (vast/min-max with werkrooster) */}
      {hasWerkrooster && monthlyBreakdown.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-main">Maandoverzicht {selectedYear}</h3>
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(parseInt(e.target.value, 10))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
            >
              {[2024, 2025, 2026, 2027].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-5 py-3 text-xs font-medium text-muted uppercase">Maand</th>
                  <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Verwacht</th>
                  <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Gewerkt</th>
                  <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Verlof</th>
                  <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Ziekte</th>
                  <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Saldo</th>
                  <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Cumulatief</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {monthlyBreakdown.map(ms => {
                  const isCurrentMonth = ms.month === new Date().getMonth() && ms.year === new Date().getFullYear();
                  const isExpanded = expandedPeriod === ms.from;
                  const hasData = ms.actual > 0 || ms.leave > 0 || ms.sick > 0;

                  // Day-by-day entries for expanded view
                  const monthDayEntries = isExpanded ? timeEntries
                    .filter(te => te.date >= ms.from && te.date <= ms.to)
                    .sort((a, b) => a.date.localeCompare(b.date) || (a.clock_in || '').localeCompare(b.clock_in || ''))
                    : [];
                  const monthLeaveEntries = isExpanded ? leaveEntries
                    .filter(le => le.date >= ms.from && le.date <= ms.to)
                    : [];

                  return (
                    <React.Fragment key={ms.from}>
                      <tr
                        className={`${isCurrentMonth ? 'bg-indigo-50/50' : 'hover:bg-gray-50'} ${hasData ? 'cursor-pointer' : ''}`}
                        onClick={() => hasData && setExpandedPeriod(isExpanded ? null : ms.from)}
                      >
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            {hasData && (
                              <svg className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            )}
                            <p className="font-medium text-main capitalize">{ms.label}</p>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right text-muted">{ms.scheduled > 0 ? formatHours(ms.scheduled) : '-'}</td>
                        <td className="px-3 py-3 text-right font-medium text-main">{ms.actual > 0 ? formatHours(ms.actual) : '-'}</td>
                        <td className="px-3 py-3 text-right text-amber-600">{ms.leave > 0 ? formatHours(ms.leave) : '-'}</td>
                        <td className="px-3 py-3 text-right text-red-600">{ms.sick > 0 ? formatHours(ms.sick) : '-'}</td>
                        <td className={`px-3 py-3 text-right font-medium ${ms.delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {ms.scheduled > 0 ? `${ms.delta >= 0 ? '+' : ''}${formatHours(ms.delta)}` : '-'}
                        </td>
                        <td className={`px-3 py-3 text-right font-semibold ${ms.cumulativeSaldo >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {ms.cumulativeSaldo >= 0 ? '+' : ''}{formatHours(ms.cumulativeSaldo)}
                        </td>
                      </tr>

                      {/* Expanded day-by-day */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={7} className="p-0">
                            <div className="bg-slate-50 border-y border-slate-200">
                              {entryError && (
                                <div className="mx-12 mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                  {entryError}
                                </div>
                              )}
                              <table className="w-full text-left text-xs">
                                <thead>
                                  <tr className="bg-slate-100">
                                    <th className="pl-12 pr-3 py-2 font-medium text-slate-500 uppercase">Datum</th>
                                    <th className="px-3 py-2 font-medium text-slate-500 uppercase">Type</th>
                                    <th className="px-3 py-2 font-medium text-slate-500 uppercase text-right">In</th>
                                    <th className="px-3 py-2 font-medium text-slate-500 uppercase text-right">Uit</th>
                                    <th className="px-3 py-2 font-medium text-slate-500 uppercase text-right">Pauze</th>
                                    <th className="px-3 py-2 font-medium text-slate-500 uppercase text-right">Netto</th>
                                    <th className="px-3 py-2 w-8" />
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                  {/* Add new entry row */}
                                  {addingEntry === ms.from && (
                                    <tr className="bg-indigo-50/50">
                                      <td className="pl-12 pr-3 py-1.5">
                                        <input type="date" className="w-32 text-xs border rounded px-1.5 py-1" value={newEntry.date} onChange={e => setNewEntry(prev => ({ ...prev, date: e.target.value }))} />
                                      </td>
                                      <td className="px-3 py-1.5">
                                        <select className="text-xs border rounded px-1 py-1" value={newEntry.type} onChange={e => {
                                          const type = e.target.value;
                                          setNewEntry(prev => {
                                            const brk = prev.clock_in && prev.clock_out ? breakForRange(type, prev.clock_in, prev.clock_out).breakMinutes : 0;
                                            return { ...prev, type, break_minutes: brk };
                                          });
                                        }}>
                                          <option value="">—</option>
                                          {SHIFT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                      </td>
                                      <td className="px-3 py-1.5 text-right"><input type="time" className="w-20 text-xs border rounded px-1.5 py-1 text-right font-mono" value={newEntry.clock_in} onChange={e => {
                                        const ci = e.target.value;
                                        setNewEntry(prev => {
                                          const brk = ci && prev.clock_out ? breakForRange(prev.type, ci, prev.clock_out).breakMinutes : prev.break_minutes;
                                          return { ...prev, clock_in: ci, break_minutes: brk };
                                        });
                                      }} /></td>
                                      <td className="px-3 py-1.5 text-right"><input type="time" className="w-20 text-xs border rounded px-1.5 py-1 text-right font-mono" value={newEntry.clock_out} onChange={e => {
                                        const co = e.target.value;
                                        setNewEntry(prev => {
                                          const brk = prev.clock_in && co ? breakForRange(prev.type, prev.clock_in, co).breakMinutes : prev.break_minutes;
                                          return { ...prev, clock_out: co, break_minutes: brk };
                                        });
                                      }} /></td>
                                      <td className="px-3 py-1.5 text-right"><input type="number" min="0" step="5" className="w-14 text-xs border rounded px-1.5 py-1 text-right" value={newEntry.break_minutes} onChange={e => setNewEntry(prev => ({ ...prev, break_minutes: parseInt(e.target.value) || 0 }))} /></td>
                                      <td className="px-3 py-1.5" />
                                      <td className="px-3 py-1.5">
                                        <div className="flex gap-1">
                                          <button onClick={handleCreateEntry} disabled={isSaving || !newEntry.clock_in || !newEntry.clock_out || !newEntry.date} className="text-emerald-600 hover:text-emerald-800 disabled:opacity-30" title="Opslaan">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                          </button>
                                          <button onClick={() => { setAddingEntry(null); setNewEntry({ date: '', type: '', clock_in: '', clock_out: '', break_minutes: 0 }); }} className="text-slate-400 hover:text-slate-600" title="Annuleren">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                  {monthDayEntries.map(te => {
                                    const d = new Date(te.date + 'T00:00:00');
                                    const dayLabel = format(d, 'EEE d MMM', { locale: nl });
                                    const clockIn = toHHmm(te.clock_in);
                                    const clockOut = toHHmm(te.clock_out);
                                    const shiftType = extractShiftType(te.notes);
                                    const isEditing = editingEntryId === te.id;
                                    return (
                                      <tr key={te.id} className={`group hover:bg-slate-100/50 ${isEditing ? 'bg-amber-50/50' : ''}`}>
                                        <td className="pl-12 pr-3 py-2 capitalize text-slate-700">{dayLabel}</td>
                                        <td className="px-3 py-2">
                                          {isEditing ? (
                                            <select className="text-xs border rounded px-1 py-0.5" value={extractShiftType(editValues.notes ?? te.notes)} onChange={e => {
                                              const newType = e.target.value;
                                              const newNotes = buildNotes(editValues.notes ?? te.notes, newType);
                                              const ci = editValues.clock_in || clockIn || '';
                                              const co = editValues.clock_out || clockOut || '';
                                              if (!breakManuallyEdited && ci && co) {
                                                setEditValues(prev => ({ ...prev, notes: newNotes, break_minutes: breakForRange(newType, ci, co).breakMinutes }));
                                              } else {
                                                setEditValues(prev => ({ ...prev, notes: newNotes }));
                                              }
                                            }}>
                                              <option value="">—</option>
                                              {SHIFT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                          ) : (
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium cursor-pointer ${shiftColors[shiftType] || 'bg-gray-100 text-gray-600'}`} onClick={() => startEditing(te)}>
                                              {shiftType || <span className="text-slate-300">+</span>}
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-slate-700">
                                          {isEditing ? (
                                            <input type="time" className="w-20 text-xs border rounded px-1.5 py-0.5 text-right font-mono" value={editValues.clock_in || ''} onChange={e => {
                                              const ci = e.target.value;
                                              const co = editValues.clock_out || clockOut || '';
                                              const notes = editValues.notes ?? te.notes;
                                              setEditValues(prev => ({
                                                ...prev,
                                                clock_in: ci,
                                                ...(!breakManuallyEdited && ci && co ? { break_minutes: recalcBreak(ci, co, notes) } : {}),
                                              }));
                                            }} onKeyDown={e => { if (e.key === 'Escape') cancelEditing(); if (e.key === 'Enter') handleSaveEdit(te.id, te); }} />
                                          ) : (
                                            <span className="cursor-pointer hover:text-indigo-600" onClick={() => startEditing(te)}>{clockIn || '-'}</span>
                                          )}
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono text-slate-700">
                                          {isEditing ? (
                                            <input type="time" className="w-20 text-xs border rounded px-1.5 py-0.5 text-right font-mono" value={editValues.clock_out || ''} onChange={e => {
                                              const co = e.target.value;
                                              const ci = editValues.clock_in || clockIn || '';
                                              const notes = editValues.notes ?? te.notes;
                                              setEditValues(prev => ({
                                                ...prev,
                                                clock_out: co,
                                                ...(!breakManuallyEdited && ci && co ? { break_minutes: recalcBreak(ci, co, notes) } : {}),
                                              }));
                                            }} onKeyDown={e => { if (e.key === 'Escape') cancelEditing(); if (e.key === 'Enter') handleSaveEdit(te.id, te); }} />
                                          ) : (
                                            <span className="cursor-pointer hover:text-indigo-600" onClick={() => startEditing(te)}>{clockOut || <span className="text-amber-500">open</span>}</span>
                                          )}
                                        </td>
                                        <td className="px-3 py-2 text-right text-slate-500">
                                          {isEditing ? (
                                            <input type="number" min="0" step="5" className="w-14 text-xs border rounded px-1.5 py-0.5 text-right" value={editValues.break_minutes ?? te.break_minutes} onChange={e => {
                                              setBreakManuallyEdited(true);
                                              setEditValues(prev => ({ ...prev, break_minutes: parseInt(e.target.value) || 0 }));
                                            }} onKeyDown={e => { if (e.key === 'Escape') cancelEditing(); if (e.key === 'Enter') handleSaveEdit(te.id, te); }} />
                                          ) : (
                                            <span className="cursor-pointer hover:text-indigo-600" onClick={() => startEditing(te)}>{te.break_minutes > 0 ? `${te.break_minutes}m` : '-'}</span>
                                          )}
                                        </td>
                                        <td className="px-3 py-2 text-right font-medium text-slate-800">{te.total_hours != null ? formatHours(te.total_hours) : '-'}</td>
                                        <td className="px-3 py-2 text-right">
                                          {isEditing ? (
                                            <div className="flex gap-1 justify-end">
                                              <button onClick={() => handleSaveEdit(te.id, te)} disabled={isSaving} className="text-emerald-600 hover:text-emerald-800 disabled:opacity-30" title="Opslaan">
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                              </button>
                                              <button onClick={cancelEditing} className="text-slate-400 hover:text-slate-600" title="Annuleren">
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                              </button>
                                            </div>
                                          ) : confirmDeleteId === te.id ? (
                                            <div className="flex gap-1 justify-end items-center">
                                              <span className="text-[10px] text-red-600">Wis?</span>
                                              <button onClick={() => handleDeleteEntry(te.id)} disabled={isSaving} className="text-red-600 hover:text-red-800 disabled:opacity-30" title="Bevestigen">
                                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                              </button>
                                              <button onClick={() => setConfirmDeleteId(null)} className="text-slate-400 hover:text-slate-600" title="Annuleren">
                                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                              </button>
                                            </div>
                                          ) : (
                                            <button onClick={() => setConfirmDeleteId(te.id)} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Verwijderen">
                                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  {monthLeaveEntries.map(le => {
                                    const d = new Date(le.date + 'T00:00:00');
                                    const dayLabel = format(d, 'EEE d MMM', { locale: nl });
                                    return (
                                      <tr key={`leave-${le.id}`} className="hover:bg-slate-100/50">
                                        <td className="pl-12 pr-3 py-2 capitalize text-slate-700">{dayLabel}</td>
                                        <td colSpan={3} className="px-3 py-2">
                                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getLeaveColor(le.leave_type)}`}>
                                            {getLeaveLabel(le.leave_type)}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2" />
                                        <td className="px-3 py-2 text-right font-medium text-amber-600">{formatHours(le.hours)}</td>
                                        <td className="px-3 py-2" />
                                      </tr>
                                    );
                                  })}
                                  {monthDayEntries.length === 0 && monthLeaveEntries.length === 0 && !addingEntry && (
                                    <tr><td colSpan={7} className="pl-12 py-3 text-slate-400">Geen registraties deze maand</td></tr>
                                  )}
                                </tbody>
                              </table>
                              {addingEntry !== ms.from && (
                                <div className="px-12 py-2 border-t border-slate-200">
                                  <button onClick={() => { setAddingEntry(ms.from); setNewEntry({ date: ms.from > format(new Date(), 'yyyy-MM-dd') ? ms.from : format(new Date(), 'yyyy-MM-dd'), type: '', clock_in: '', clock_out: '', break_minutes: 0 }); }} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1">
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                    Toevoegen
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50">
                  <td className="px-5 py-3 font-semibold text-main">Totaal</td>
                  <td className="px-3 py-3 text-right font-semibold text-muted">{formatHours(monthlyBreakdown.reduce((s, m) => s + m.scheduled, 0))}</td>
                  <td className="px-3 py-3 text-right font-semibold text-main">{formatHours(monthlyBreakdown.reduce((s, m) => s + m.actual, 0))}</td>
                  <td className="px-3 py-3 text-right font-semibold text-amber-600">{formatHours(monthlyBreakdown.reduce((s, m) => s + m.leave, 0))}</td>
                  <td className="px-3 py-3 text-right font-semibold text-red-600">{formatHours(monthlyBreakdown.reduce((s, m) => s + m.sick, 0))}</td>
                  <td className="px-3 py-3" />
                  <td className={`px-3 py-3 text-right font-bold ${(yearSaldo ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {yearSaldo !== null ? `${yearSaldo >= 0 ? '+' : ''}${formatHours(yearSaldo)}` : '-'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Period history table (0-uren / no werkrooster) */}
      {!hasWerkrooster && (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-main">Periodes</h3>
          <select
            value={selectedYear}
            onChange={e => setSelectedYear(parseInt(e.target.value, 10))}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
          >
            {[2024, 2025, 2026, 2027].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-5 py-3 text-xs font-medium text-muted uppercase">Periode</th>
                <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Gewerkt</th>
                <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Uitbet.</th>
                {!isZzp && <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">30%</th>}
                {!isZzp && <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">50%</th>}
                {!isZzp && <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">150%</th>}
                {!isZzp && <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Overuren</th>}
                <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Verlof</th>
                <th className="px-3 py-3 text-xs font-medium text-muted uppercase text-right">Ziekte</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {periodSummaries.map(ps => {
                const isCurrent = ps.period.from === currentPeriod.from;
                const isExpanded = expandedPeriod === ps.period.from;
                const hasHours = ps.workedHours > 0 || ps.leaveHours > 0 || ps.sickHours > 0;

                // Get daily time entries for this period when expanded
                const periodDayEntries = isExpanded ? timeEntries
                  .filter(te => te.date >= ps.period.from && te.date <= ps.period.to)
                  .sort((a, b) => a.date.localeCompare(b.date) || (a.clock_in || '').localeCompare(b.clock_in || ''))
                  : [];

                // Get leave entries for this period
                const periodLeaveEntries = isExpanded ? leaveEntries
                  .filter(le => le.date >= ps.period.from && le.date <= ps.period.to)
                  : [];

                return (
                  <React.Fragment key={ps.period.from}>
                    <tr
                      className={`${isCurrent ? 'bg-indigo-50/50' : 'hover:bg-gray-50'} ${hasHours ? 'cursor-pointer' : ''}`}
                      onClick={() => hasHours && setExpandedPeriod(isExpanded ? null : ps.period.from)}
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          {hasHours && (
                            <svg className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          )}
                          <div>
                            <p className="font-medium text-main capitalize">{ps.period.label}</p>
                            <p className="text-[10px] text-muted">{ps.period.from.slice(8)}-{ps.period.from.slice(5, 7)} — {ps.period.to.slice(8)}-{ps.period.to.slice(5, 7)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right font-medium text-main">{formatHours(ps.workedHours)}</td>
                      <td className="px-3 py-3 text-right text-main">{formatHours(ps.paidHours)}</td>
                      {!isZzp && (() => {
                        const s30 = ps.surcharges.breakdown.find(b => b.percentage === 0.30);
                        const s50 = ps.surcharges.breakdown.find(b => b.percentage === 0.50);
                        const s150 = ps.surcharges.breakdown.find(b => b.percentage === 1.50);
                        return (
                          <>
                            <td className="px-3 py-3 text-right text-emerald-600 text-xs">
                              {s30 ? `${formatHours(s30.hours)} (${formatEuro(s30.amount)})` : '-'}
                            </td>
                            <td className="px-3 py-3 text-right text-emerald-600 text-xs">
                              {s50 ? `${formatHours(s50.hours)} (${formatEuro(s50.amount)})` : '-'}
                            </td>
                            <td className="px-3 py-3 text-right text-emerald-600 text-xs">
                              {s150 ? `${formatHours(s150.hours)} (${formatEuro(s150.amount)})` : '-'}
                            </td>
                          </>
                        );
                      })()}
                      {!isZzp && (
                        <td className="px-3 py-3 text-right">
                          {ps.overtimeSurplus > 0 ? (
                            <span className="text-indigo-600 font-medium">+{formatHours(ps.overtimeSurplus)}</span>
                          ) : '-'}
                        </td>
                      )}
                      <td className="px-3 py-3 text-right text-amber-600">
                        {ps.leaveHours > 0 ? formatHours(ps.leaveHours) : '-'}
                      </td>
                      <td className="px-3 py-3 text-right text-red-600">
                        {ps.sickHours > 0 ? formatHours(ps.sickHours) : '-'}
                      </td>
                    </tr>

                    {/* Expanded day-by-day breakdown */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={isZzp ? 4 : 9} className="p-0">
                          <div className="bg-slate-50 border-y border-slate-200">
                            <table className="w-full text-left text-xs">
                              <thead>
                                <tr className="bg-slate-100">
                                  <th className="pl-12 pr-3 py-2 font-medium text-slate-500 uppercase">Datum</th>
                                  <th className="px-3 py-2 font-medium text-slate-500 uppercase">Type</th>
                                  <th className="px-3 py-2 font-medium text-slate-500 uppercase text-right">In</th>
                                  <th className="px-3 py-2 font-medium text-slate-500 uppercase text-right">Uit</th>
                                  <th className="px-3 py-2 font-medium text-slate-500 uppercase text-right">Pauze</th>
                                  <th className="px-3 py-2 font-medium text-slate-500 uppercase text-right">Netto</th>
                                  <th className="px-3 py-2 w-8" />
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {/* Add new entry row */}
                                {addingEntry === ps.period.from && (
                                  <tr className="bg-indigo-50/50">
                                    <td className="pl-12 pr-3 py-1.5">
                                      <input type="date" className="w-32 text-xs border rounded px-1.5 py-1" value={newEntry.date} onChange={e => setNewEntry(prev => ({ ...prev, date: e.target.value }))} />
                                    </td>
                                    <td className="px-3 py-1.5">
                                      <select className="text-xs border rounded px-1 py-1" value={newEntry.type} onChange={e => {
                                        const type = e.target.value;
                                        setNewEntry(prev => {
                                          const brk = prev.clock_in && prev.clock_out ? breakForRange(type, prev.clock_in, prev.clock_out).breakMinutes : 0;
                                          return { ...prev, type, break_minutes: brk };
                                        });
                                      }}>
                                        <option value="">—</option>
                                        {SHIFT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                      </select>
                                    </td>
                                    <td className="px-3 py-1.5 text-right"><input type="time" className="w-20 text-xs border rounded px-1.5 py-1 text-right font-mono" value={newEntry.clock_in} onChange={e => {
                                      const ci = e.target.value;
                                      setNewEntry(prev => {
                                        const brk = ci && prev.clock_out ? breakForRange(prev.type, ci, prev.clock_out).breakMinutes : prev.break_minutes;
                                        return { ...prev, clock_in: ci, break_minutes: brk };
                                      });
                                    }} /></td>
                                    <td className="px-3 py-1.5 text-right"><input type="time" className="w-20 text-xs border rounded px-1.5 py-1 text-right font-mono" value={newEntry.clock_out} onChange={e => {
                                      const co = e.target.value;
                                      setNewEntry(prev => {
                                        const brk = prev.clock_in && co ? breakForRange(prev.type, prev.clock_in, co).breakMinutes : prev.break_minutes;
                                        return { ...prev, clock_out: co, break_minutes: brk };
                                      });
                                    }} /></td>
                                    <td className="px-3 py-1.5 text-right"><input type="number" min="0" step="5" className="w-14 text-xs border rounded px-1.5 py-1 text-right" value={newEntry.break_minutes} onChange={e => setNewEntry(prev => ({ ...prev, break_minutes: parseInt(e.target.value) || 0 }))} /></td>
                                    <td className="px-3 py-1.5" />
                                    <td className="px-3 py-1.5">
                                      <div className="flex gap-1">
                                        <button onClick={handleCreateEntry} disabled={isSaving || !newEntry.clock_in || !newEntry.clock_out || !newEntry.date} className="text-emerald-600 hover:text-emerald-800 disabled:opacity-30" title="Opslaan">
                                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                        </button>
                                        <button onClick={() => { setAddingEntry(null); setNewEntry({ date: '', type: '', clock_in: '', clock_out: '', break_minutes: 0 }); }} className="text-slate-400 hover:text-slate-600" title="Annuleren">
                                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                                {/* Time entries */}
                                {periodDayEntries.map(te => {
                                  const d = new Date(te.date + 'T00:00:00');
                                  const dayLabel = format(d, 'EEE d MMM', { locale: nl });
                                  const clockIn = toHHmm(te.clock_in);
                                  const clockOut = toHHmm(te.clock_out);
                                  const scheduleForDate = scheduleEntries.find(s => s.staff_id === staff.id && s.date === te.date);
                                  const scheduleShift = (scheduleForDate?.shift_type ?? null) as ShiftTypeValue | null;
                                  const isEditing = editingEntryId === te.id;
                                  return (
                                    <tr key={te.id} className={`group hover:bg-slate-100/50 ${isEditing ? 'bg-amber-50/50' : ''}`}>
                                      <td className="pl-12 pr-3 py-2 capitalize text-slate-700">{dayLabel}</td>
                                      <td className="px-3 py-2">
                                        <ShiftTypeSelector
                                          staffId={staff.id}
                                          date={te.date}
                                          currentShift={scheduleShift}
                                          scheduleId={scheduleForDate?.id ?? null}
                                          defaultTimeFrom={clockIn ?? '07:00'}
                                          defaultTimeTo={clockOut ?? '16:00'}
                                          onSaved={result => {
                                            setScheduleEntries(prev => {
                                              const existing = prev.find(s => s.staff_id === staff.id && s.date === te.date);
                                              if (existing && result.scheduleId) {
                                                return prev.map(s =>
                                                  s.id === existing.id ? { ...s, shift_type: result.shiftType, id: result.scheduleId! } : s
                                                );
                                              }
                                              if (!existing && result.scheduleId && result.shiftType) {
                                                return [...prev, { id: result.scheduleId, staff_id: staff.id, date: te.date, shift_type: result.shiftType, time_from: clockIn ?? '07:00', time_to: clockOut ?? '16:00', salesforce_route_id: null, notes: null }];
                                              }
                                              return prev;
                                            });
                                          }}
                                        />
                                      </td>
                                      <td className="px-3 py-2 text-right font-mono text-slate-700">
                                        {isEditing ? (
                                          <input type="time" className="w-20 text-xs border rounded px-1.5 py-0.5 text-right font-mono" value={editValues.clock_in || ''} onChange={e => {
                                            const ci = e.target.value;
                                            const co = editValues.clock_out || clockOut || '';
                                            const notes = editValues.notes ?? te.notes;
                                            setEditValues(prev => ({
                                              ...prev,
                                              clock_in: ci,
                                              ...(!breakManuallyEdited && ci && co ? { break_minutes: recalcBreak(ci, co, notes) } : {}),
                                            }));
                                          }} onKeyDown={e => { if (e.key === 'Escape') cancelEditing(); if (e.key === 'Enter') handleSaveEdit(te.id, te); }} />
                                        ) : (
                                          <span className="cursor-pointer hover:text-indigo-600" onClick={() => startEditing(te)}>{clockIn || '-'}</span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-right font-mono text-slate-700">
                                        {isEditing ? (
                                          <input type="time" className="w-20 text-xs border rounded px-1.5 py-0.5 text-right font-mono" value={editValues.clock_out || ''} onChange={e => {
                                            const co = e.target.value;
                                            const ci = editValues.clock_in || clockIn || '';
                                            const notes = editValues.notes ?? te.notes;
                                            setEditValues(prev => ({
                                              ...prev,
                                              clock_out: co,
                                              ...(!breakManuallyEdited && ci && co ? { break_minutes: recalcBreak(ci, co, notes) } : {}),
                                            }));
                                          }} onKeyDown={e => { if (e.key === 'Escape') cancelEditing(); if (e.key === 'Enter') handleSaveEdit(te.id, te); }} />
                                        ) : (
                                          <span className="cursor-pointer hover:text-indigo-600" onClick={() => startEditing(te)}>{clockOut || <span className="text-amber-500">open</span>}</span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-right text-slate-500">
                                        {isEditing ? (
                                          <input type="number" min="0" step="5" className="w-14 text-xs border rounded px-1.5 py-0.5 text-right" value={editValues.break_minutes ?? te.break_minutes} onChange={e => {
                                            setBreakManuallyEdited(true);
                                            setEditValues(prev => ({ ...prev, break_minutes: parseInt(e.target.value) || 0 }));
                                          }} onKeyDown={e => { if (e.key === 'Escape') cancelEditing(); if (e.key === 'Enter') handleSaveEdit(te.id, te); }} />
                                        ) : (
                                          <span className="cursor-pointer hover:text-indigo-600" onClick={() => startEditing(te)}>{te.break_minutes > 0 ? `${te.break_minutes}m` : '-'}</span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-right font-medium text-slate-800">{te.total_hours != null ? formatHours(te.total_hours) : '-'}</td>
                                      <td className="px-3 py-2 text-right">
                                        {isEditing ? (
                                          <div className="flex gap-1 justify-end">
                                            <button onClick={() => handleSaveEdit(te.id, te)} disabled={isSaving} className="text-emerald-600 hover:text-emerald-800 disabled:opacity-30" title="Opslaan">
                                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                            </button>
                                            <button onClick={cancelEditing} className="text-slate-400 hover:text-slate-600" title="Annuleren">
                                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                            </button>
                                          </div>
                                        ) : confirmDeleteId === te.id ? (
                                          <div className="flex gap-1 justify-end items-center">
                                            <span className="text-[10px] text-red-600">Wis?</span>
                                            <button onClick={() => handleDeleteEntry(te.id)} disabled={isSaving} className="text-red-600 hover:text-red-800 disabled:opacity-30" title="Bevestigen">
                                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                            </button>
                                            <button onClick={() => setConfirmDeleteId(null)} className="text-slate-400 hover:text-slate-600" title="Annuleren">
                                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                            </button>
                                          </div>
                                        ) : (
                                          <button onClick={() => setConfirmDeleteId(te.id)} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Verwijderen">
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                                {/* Leave entries (shown as separate rows) */}
                                {periodLeaveEntries.map(le => {
                                  const d = new Date(le.date + 'T00:00:00');
                                  const dayLabel = format(d, 'EEE d MMM', { locale: nl });
                                  return (
                                    <tr key={`leave-${le.id}`} className="hover:bg-slate-100/50">
                                      <td className="pl-12 pr-3 py-2 capitalize text-slate-700">{dayLabel}</td>
                                      <td colSpan={3} className="px-3 py-2">
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getLeaveColor(le.leave_type)}`}>
                                          {getLeaveLabel(le.leave_type)}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2" />
                                      <td className="px-3 py-2 text-right font-medium text-amber-600">{formatHours(le.hours)}</td>
                                      <td className="px-3 py-2" />
                                    </tr>
                                  );
                                })}
                                {periodDayEntries.length === 0 && periodLeaveEntries.length === 0 && !addingEntry && (
                                  <tr><td colSpan={7} className="pl-12 py-3 text-slate-400">Geen registraties in deze periode</td></tr>
                                )}
                              </tbody>
                              {/* Totals footer */}
                              {(periodDayEntries.length > 0 || periodLeaveEntries.length > 0) && (
                                <tfoot>
                                  <tr className="bg-slate-100 border-t border-slate-200">
                                    <td className="pl-12 pr-3 py-2 font-semibold text-slate-600">
                                      Totaal ({periodDayEntries.length} {periodDayEntries.length === 1 ? 'dienst' : 'diensten'})
                                    </td>
                                    <td className="px-3 py-2" />
                                    <td className="px-3 py-2" />
                                    <td className="px-3 py-2" />
                                    <td className="px-3 py-2 text-right text-slate-500">
                                      {(() => { const total = periodDayEntries.reduce((s, te) => s + te.break_minutes, 0); return total > 0 ? `${total}m` : '-'; })()}
                                    </td>
                                    <td className="px-3 py-2 text-right font-semibold text-slate-800">{formatHours(ps.workedHours)}</td>
                                    <td className="px-3 py-2" />
                                  </tr>
                                </tfoot>
                              )}
                            </table>
                            {addingEntry !== ps.period.from && (
                              <div className="px-12 py-2 border-t border-slate-200">
                                <button onClick={() => { setAddingEntry(ps.period.from); setNewEntry({ date: ps.period.from > format(new Date(), 'yyyy-MM-dd') ? ps.period.from : format(new Date(), 'yyyy-MM-dd'), type: '', clock_in: '', clock_out: '', break_minutes: 0 }); }} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1">
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                  Toevoegen
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Daily saldo breakdown (current period, vast/min-max only) */}
      {hasWerkrooster && currentScheduled && currentScheduled.dailyBreakdown.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-main">
              Dagelijks saldo — {currentPeriod.label}
              {saldoIndicatief && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-medium align-middle">
                  indicatief (min-max)
                </span>
              )}
            </h3>
            <p className="text-[10px] text-muted">
              {currentPeriod.from} t/m {currentPeriod.to} · Verwacht = rooster-uren − pauze volgens dezelfde regels als de klok (hover voor de berekening)
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-5 py-2 text-xs font-medium text-muted uppercase">Datum</th>
                  <th className="px-3 py-2 text-xs font-medium text-muted uppercase text-right">Verwacht</th>
                  <th className="px-3 py-2 text-xs font-medium text-muted uppercase text-right">Werkelijk</th>
                  <th className="px-3 py-2 text-xs font-medium text-muted uppercase text-right">Verlof</th>
                  <th className="px-3 py-2 text-xs font-medium text-muted uppercase text-right">Saldo</th>
                  <th className="px-3 py-2 text-xs font-medium text-muted uppercase text-right">Cumulatief</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentScheduled.dailyBreakdown.map(day => {
                  const d = new Date(day.date + 'T00:00:00');
                  const dayLabel = format(d, 'EEE d MMM', { locale: nl });
                  return (
                    <tr key={day.date} className={day.isHoliday ? 'bg-purple-50/50' : day.isLeaveDay ? 'bg-amber-50/50' : ''}>
                      <td className="px-5 py-2 text-xs capitalize">
                        {dayLabel}
                        {day.isHoliday && <span className="ml-1 text-purple-500 text-[10px]">feestdag</span>}
                        {day.isLeaveDay && !day.isHoliday && <span className="ml-1 text-amber-500 text-[10px]">verlof</span>}
                      </td>
                      <td
                        className="px-3 py-2 text-xs text-right text-muted"
                        title={day.expected > 0 && day.roosterFrom
                          ? `Rooster ${day.roosterFrom}-${day.roosterTo} (${formatHours(day.brutoHours ?? 0)} bruto) − ${day.breakMinutes ?? 0}m pauze (${day.shiftType}, via ${day.breakSource === 'schedule' ? 'rooster-shift' : 'team'})`
                          : undefined}
                      >
                        {day.expected > 0 ? formatHours(day.expected) : '-'}
                      </td>
                      <td className="px-3 py-2 text-xs text-right font-medium text-main">{day.actual > 0 ? formatHours(day.actual) : '-'}</td>
                      <td className="px-3 py-2 text-xs text-right text-amber-600">{day.leave > 0 ? formatHours(day.leave) : '-'}</td>
                      <td className={`px-3 py-2 text-xs text-right font-medium ${day.delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {day.delta >= 0 ? '+' : ''}{formatHours(day.delta)}
                      </td>
                      <td className={`px-3 py-2 text-xs text-right font-medium ${day.cumulativeDelta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {day.cumulativeDelta >= 0 ? '+' : ''}{formatHours(day.cumulativeDelta)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50">
                  <td className="px-5 py-2 text-xs font-semibold text-main">Totaal</td>
                  <td className="px-3 py-2 text-xs text-right font-semibold text-muted">{formatHours(currentScheduled.totalScheduled)}</td>
                  <td className="px-3 py-2 text-xs text-right font-semibold text-main">{formatHours(currentScheduled.totalActual)}</td>
                  <td className="px-3 py-2 text-xs text-right font-semibold text-amber-600">{currentScheduled.totalLeave > 0 ? formatHours(currentScheduled.totalLeave) : '-'}</td>
                  <td className={`px-3 py-2 text-xs text-right font-semibold ${currentScheduled.saldo >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {currentScheduled.saldo >= 0 ? '+' : ''}{formatHours(currentScheduled.saldo)}
                  </td>
                  <td className="px-3 py-2" />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Leave entitlements (potjes) */}
      {leaveEntitlements.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-main">Verlofpotjes</h3>
            <button
              onClick={() => setShowEntitlementForm(true)}
              className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              + Potje toevoegen
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {leaveEntitlements.map(ent => {
              // Vakantie-potjes (overdracht, compensatie) zijn een laag in het vakantiesaldo
              // hierboven: FIFO op vervaldatum bepaalt wat eraf gaat, niet "alle vakantie-uren
              // van het jaar". Toon hier de laag-stand, geen eigen telling.
              const laag = ent.leave_type === 'vakantie' ? vakantieSaldo?.lagen.find(l => l.entitlement_id === ent.id) ?? null : null;
              const isExpired = ent.valid_until < (new Date().toISOString().split('T')[0] ?? '');
              if (ent.leave_type === 'vakantie') {
                return (
                  <div key={ent.id} className="px-5 py-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${getLeaveColor(ent.leave_type)}`}>
                          {getLeaveLabel(ent.leave_type)}
                        </span>
                        {ent.description && <span className="text-xs text-muted ml-2">{ent.description}</span>}
                      </div>
                      <button onClick={() => deleteEntitlement(ent.id)} className="text-xs text-red-500 hover:text-red-700">Verwijder</button>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                      <span className="text-muted">Tegoed: <span className="font-medium text-main">{formatHours(ent.total_hours)}</span></span>
                      {laag ? (
                        <>
                          <span className="text-muted">Via saldo opgenomen: <span className="font-medium text-main">{formatHours(laag.opgenomen)}</span></span>
                          <span className={`font-semibold ${laag.status === 'vervallen' ? 'text-muted line-through' : laag.resterend > 0 ? 'text-emerald-600' : 'text-muted'}`}>
                            Rest: {formatHours(laag.status === 'vervallen' ? laag.vervallen : laag.resterend)}
                          </span>
                          <span className="text-[10px] text-muted">{laag.laag} {laag.jaar}</span>
                        </>
                      ) : (
                        <span className="text-[10px] text-muted">telt mee in het vakantiesaldo hierboven</span>
                      )}
                      <span className={`text-[10px] ml-auto ${isExpired ? 'text-red-500 font-medium' : 'text-muted'}`}>
                        {isExpired ? 'Vervallen' : `vervalt ${format(new Date(ent.valid_until + 'T00:00:00'), 'd MMM yyyy', { locale: nl })}`}
                      </span>
                    </div>
                    <p className="mt-1 text-[10px] text-muted">Wordt pas aangesproken als er geen eerder vervallende laag meer is (FIFO op vervaldatum); zie de vakantiekaart.</p>
                  </div>
                );
              }

              const used = leaveEntries
                .filter(l => l.staff_id === staff.id && l.leave_type === ent.leave_type && l.date >= ent.valid_from && l.date <= ent.valid_until)
                .reduce((sum, l) => sum + l.hours, 0);
              const remaining = ent.total_hours - used;
              const pct = ent.total_hours > 0 ? Math.min(100, (used / ent.total_hours) * 100) : 0;

              return (
                <div key={ent.id} className="px-5 py-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${getLeaveColor(ent.leave_type)}`}>
                        {getLeaveLabel(ent.leave_type)}
                      </span>
                      {ent.description && <span className="text-xs text-muted ml-2">{ent.description}</span>}
                    </div>
                    <button onClick={() => deleteEntitlement(ent.id)} className="text-xs text-red-500 hover:text-red-700">Verwijder</button>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-muted">Tegoed: <span className="font-medium text-main">{formatHours(ent.total_hours)}</span></span>
                    <span className="text-muted">Opgenomen: <span className="font-medium text-main">{formatHours(used)}</span></span>
                    <span className={`font-semibold ${remaining > 0 ? 'text-emerald-600' : 'text-muted'}`}>
                      Rest: {formatHours(remaining)}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-3">
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-cyan-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className={`text-[10px] ${isExpired ? 'text-red-500 font-medium' : 'text-muted'}`}>
                      {isExpired ? 'Verlopen' : `tot ${format(new Date(ent.valid_until + 'T00:00:00'), 'd MMM yyyy', { locale: nl })}`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Entitlement form (inline) */}
      {(showEntitlementForm || leaveEntitlements.length === 0) && !showLeaveDialog && (
        <EntitlementForm
          staffId={staff.id}
          visible={showEntitlementForm || leaveEntitlements.length === 0}
          onSave={async (data) => {
            const result = await createEntitlement(data);
            if (!result) throw new Error('Kon potje niet aanmaken — probeer opnieuw');
            setShowEntitlementForm(false);
          }}
          onCancel={() => setShowEntitlementForm(false)}
        />
      )}

      {/* Leave history */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-main">Verlof</h3>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowLeaveDialog(true)}
              className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-50 rounded-lg transition-colors"
              title="Losse dag-invoer met handmatige uren (bv. feestdag- of ziekte-correctie)"
            >
              Handmatige correctie
            </button>
            <button
              onClick={() => setShowOnBehalfDialog(true)}
              className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              + Verlof toevoegen
            </button>
          </div>
        </div>
        {staffLeave.length === 0 ? (
          <p className="text-sm text-muted text-center py-6">Geen verlof geregistreerd</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {staffLeave.slice(0, 20).map(le => (
              <div key={le.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 rounded text-[10px] font-medium ${getLeaveColor(le.leave_type)}`}>
                    {getLeaveLabel(le.leave_type)}
                  </span>
                  <span className="text-sm text-main">
                    {format(new Date(le.date + 'T00:00:00'), 'd MMM yyyy', { locale: nl })}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted">{formatHours(le.hours)}</span>
                  <button
                    onClick={() => deleteLeave(le.id)}
                    className="text-xs text-red-500 hover:text-red-700 transition-colors"
                  >
                    Verwijder
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Verlof namens medewerker (primair pad: netto rooster-uren, meerdaags) */}
      {showOnBehalfDialog && (
        <LeaveOnBehalfDialog
          staffList={[staff]}
          preselectedStaffId={staff.id}
          onClose={() => setShowOnBehalfDialog(false)}
          onCreated={() => {
            const yearFrom = `${selectedYear}-01-01`;
            const yearTo = `${selectedYear}-12-31`;
            fetchLeave({ staff_id: staff.id, from: yearFrom, to: yearTo });
            fetchEntitlements(staff.id);
          }}
        />
      )}

      {/* Leave dialog (handmatige correctie, losse dag) */}
      {showLeaveDialog && (
        <LeaveDialog
          staffName={staff.name}
          staffId={staff.id}
          werkrooster={staff.werkrooster}
          shiftType={staff.team}
          entitlements={leaveEntitlements}
          leaveEntries={staffLeave}
          onSave={handleCreateLeave}
          onDelete={handleDeleteLeave}
          onClose={() => setShowLeaveDialog(false)}
        />
      )}
    </div>
  );
}

// ── Entitlement Form ────────────────────────────────────────────────

const ENTITLEMENT_PRESETS: { type: LeaveType; label: string; hours: (uren: number) => number; validMonths: number; description: string }[] = [
  { type: 'geboorteverlof', label: 'Geboorteverlof (1 week)', hours: (u) => u, validMonths: 1, description: 'Geboorteverlof partner — 1 week, 100% loon' },
  { type: 'aanvullend_geboorteverlof', label: 'Aanv. geboorteverlof (5 weken)', hours: (u) => u * 5, validMonths: 6, description: 'Aanvullend geboorteverlof — 5 weken, 70% via UWV' },
  { type: 'ouderschapsverlof', label: 'Ouderschapsverlof (9 weken)', hours: (u) => u * 9, validMonths: 12, description: 'Betaald ouderschapsverlof — 9 weken, 70% via UWV' },
];

function EntitlementForm({ staffId, visible, onSave, onCancel }: {
  staffId: string;
  visible: boolean;
  onSave: (data: { staff_id: string; leave_type: LeaveType; total_hours: number; description: string; valid_from: string; valid_until: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);
  const [customType, setCustomType] = useState<LeaveType>('vakantie');
  const [customHours, setCustomHours] = useState('40');
  const [customDesc, setCustomDesc] = useState('');
  const [validFrom, setValidFrom] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [validUntil, setValidUntil] = useState(format(new Date(new Date().getFullYear(), 11, 31), 'yyyy-MM-dd'));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!visible) return null;

  const handlePreset = (idx: number) => {
    const p = ENTITLEMENT_PRESETS[idx]!;
    setSelectedPreset(idx);
    setCustomType(p.type);
    setCustomDesc(p.description);
    // Calculate valid_until based on validMonths
    const until = new Date(validFrom + 'T00:00:00');
    until.setMonth(until.getMonth() + p.validMonths);
    setValidUntil(format(until, 'yyyy-MM-dd'));
  };

  const handleSave = async () => {
    setError(null);
    if (!validUntil) {
      setError('Vul een "Geldig tot" datum in');
      return;
    }
    setIsSaving(true);
    try {
      const preset = selectedPreset !== null ? ENTITLEMENT_PRESETS[selectedPreset] : null;
      // For presets, calculate hours based on staff weekly hours (default 40)
      const hours = preset ? preset.hours(40) : parseFloat(customHours) || 40;
      await onSave({
        staff_id: staffId,
        leave_type: customType,
        total_hours: hours,
        description: customDesc,
        valid_from: validFrom,
        valid_until: validUntil,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Opslaan mislukt');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <h3 className="text-sm font-semibold text-main mb-4">Verlofpotje toevoegen</h3>

      {/* Presets */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
        {ENTITLEMENT_PRESETS.map((p, i) => (
          <button
            key={p.type}
            onClick={() => handlePreset(i)}
            className={`p-3 rounded-lg border text-left text-xs transition-colors ${
              selectedPreset === i ? 'border-cyan-400 bg-cyan-50' : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <p className="font-semibold text-main">{p.label}</p>
            <p className="text-muted mt-0.5">{p.description}</p>
          </button>
        ))}
      </div>

      {/* Custom fields */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Type</label>
          <select
            value={selectedPreset !== null ? ENTITLEMENT_PRESETS[selectedPreset]!.type : customType}
            onChange={e => { setCustomType(e.target.value as LeaveType); setSelectedPreset(null); }}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            {LEAVE_TYPES.map(lt => (
              <option key={lt.value} value={lt.value}>{lt.label}</option>
            ))}
            <option value="urensaldo">Urensaldo</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Uren</label>
          <input
            type="number"
            value={selectedPreset !== null ? (ENTITLEMENT_PRESETS[selectedPreset]!.hours(40)).toString() : customHours}
            onChange={e => { setCustomHours(e.target.value); setSelectedPreset(null); }}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Geldig vanaf</label>
          <input
            type="date"
            value={validFrom}
            onChange={e => setValidFrom(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Geldig tot</label>
          <input
            type="date"
            value={validUntil}
            onChange={e => setValidUntil(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Omschrijving</label>
          <input
            type="text"
            value={customDesc}
            onChange={e => setCustomDesc(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          />
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600 mb-3">{error}</p>
      )}

      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 disabled:opacity-50"
        >
          {isSaving ? 'Opslaan...' : 'Potje aanmaken'}
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">
          Annuleren
        </button>
      </div>
    </div>
  );
}
